import { useEffect, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, Cell,
} from "recharts";
import api, { euro } from "@/lib/api";
import { StatCard } from "@/components/kit";
import { TrendingUp, Wallet, FileCheck, Receipt } from "lucide-react";

const BARS = ["#F59E0B", "#3B82F6", "#10B981", "#A855F7", "#EF4444", "#14B8A6", "#EAB308", "#EC4899"];

const tooltipStyle = {
  contentStyle: { background: "hsl(240 10% 3.9%)", border: "1px solid hsl(240 3.7% 15.9%)", borderRadius: 0, fontSize: 12 },
  formatter: (v) => euro(v),
};

export default function Reports() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data)
    return <div className="animate-pulse font-mono text-sm text-muted-foreground">Chargement des rapports…</div>;

  const hasData = data.paid_count > 0;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="reports-page">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard label="CA encaissé" value={euro(data.total_paid)} sub="Factures payées" icon={TrendingUp} accent="bg-emerald-500/15 text-emerald-400" testid="report-paid" />
        <StatCard label="En attente" value={euro(data.total_outstanding)} sub="Non encore payées" icon={Wallet} accent="bg-blue-500/15 text-blue-400" testid="report-outstanding" />
        <StatCard label="Factures payées" value={data.paid_count} sub="Nombre total" icon={FileCheck} accent="bg-primary/15 text-primary" testid="report-count" />
        <StatCard label="Panier moyen" value={euro(data.avg_invoice)} sub="Par facture payée" icon={Receipt} accent="bg-secondary text-primary" testid="report-avg" />
      </div>

      {!hasData ? (
        <div className="flex flex-col items-center justify-center border border-dashed border-border py-24 text-center">
          <TrendingUp size={40} className="mb-4 text-muted-foreground" />
          <p className="font-display text-lg font-semibold">Aucune donnée de vente pour l'instant</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Marquez des factures comme « payées » (ou encaissez-les via le lien de paiement) pour
            voir apparaître vos rapports de chiffre d'affaires.
          </p>
        </div>
      ) : (
        <>
          <div className="border border-border bg-card p-6">
            <h3 className="mb-6 font-display text-lg font-bold">Chiffre d'affaires par période</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data.revenue_by_month} margin={{ left: -10 }}>
                <defs>
                  <linearGradient id="rr" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 3.7% 15.9%)" vertical={false} />
                <XAxis dataKey="month" stroke="hsl(240 5% 64.9%)" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(240 5% 64.9%)" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip {...tooltipStyle} />
                <Area type="monotone" dataKey="total" stroke="#F59E0B" strokeWidth={2} fill="url(#rr)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="border border-border bg-card p-6">
              <h3 className="mb-6 font-display text-lg font-bold">Top clients</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.top_clients} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 3.7% 15.9%)" horizontal={false} />
                  <XAxis type="number" stroke="hsl(240 5% 64.9%)" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="name" stroke="hsl(240 5% 64.9%)" fontSize={11} width={90} tickLine={false} axisLine={false} />
                  <Tooltip {...tooltipStyle} cursor={{ fill: "hsl(240 3.7% 15.9% / 0.4)" }} />
                  <Bar dataKey="total" radius={[0, 2, 2, 0]}>
                    {data.top_clients.map((_, i) => <Cell key={i} fill={BARS[i % BARS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="border border-border bg-card p-6">
              <h3 className="mb-6 font-display text-lg font-bold">Top produits / prestations</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.top_products} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 3.7% 15.9%)" horizontal={false} />
                  <XAxis type="number" stroke="hsl(240 5% 64.9%)" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="name" stroke="hsl(240 5% 64.9%)" fontSize={11} width={90} tickLine={false} axisLine={false} />
                  <Tooltip {...tooltipStyle} cursor={{ fill: "hsl(240 3.7% 15.9% / 0.4)" }} />
                  <Bar dataKey="total" radius={[0, 2, 2, 0]}>
                    {data.top_products.map((_, i) => <Cell key={i} fill={BARS[(i + 3) % BARS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
